import { useState, useEffect } from 'react';
import { Table } from '../../../../components/Table';
import { UserDsQueryParams } from '../../../../api/types/user';
import { Key } from 'antd/es/table/interface';
import ArrowUp from '../../../../components/Icons/ArrowUp';
import ArrowDown from '../../../../components/Icons/ArrowDown';
import { PlusCircleOutlined } from '@ant-design/icons';
import { useTableDataSourceWithHooks } from '../../../../components/Table/useTableDataPropsWithHooks';
import InputSearch from '../../../../components/Fields/InputSearch';
import { Button, Empty, message, Popover, Typography } from 'antd';
import NoUsers from '../../../../components/Icons/NoUsers';
import NoResults from '../../../../components/Icons/NoResults';
import useDebounceSearch from '../../../../hooks/useDebounceSearch';
import { ComponentExpenseArea, ExpenseAreaI } from '../../../../api/types/expenseareas';
import { AddExpenseAreaModal } from '../components/AddExpenseAreaModal';
import { Dropdown, Tooltip } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import {
    useCopyEAMutation,
    useGetExpenseAreaListQuery,
    usePreviewFormDiscardChangesMutation
} from '../../../../api/expenseAreas';
import Dots from '../../../../components/Icons/dots';
import { PeopleIcon } from '../../../../components/Icons/PeopleIcon';
import InfoIcon from '../../../../components/Icons/infoIcon';
import { ChangeStatusModal } from '../components/ChangeStatusModal';
import { AdminKickOffPreviewModal } from '../components/AdminKickOffPreviewModal';
import { AdminScoreCardPreviewModal } from '../components/AdminScoreCardPreviewModal';
import { AdminSupplierProfilePreviewModal } from '../components/AdminSupplierProfilePreviewModal';
import { getMenu } from '../components/getMenu';
import EllipsisTooltipCell from '../../../../helpers/EllipsisTooltipCell';
import { useSearchParams } from 'react-router-dom';
import clocoIcon from '../../../../assets/GradeIcon.svg';

export const ExpenseAreas = () => {
    const [variables, setVariables] = useState<UserDsQueryParams>({
        filter: { search: undefined },
        per_page: 10,
        page: 1
    });
    const { data, isLoading, isFetching, refetch } = useGetExpenseAreaListQuery(variables);

    const [editModal, setEditModal] = useState<{ type: string; open: boolean; id: number | null }>({
        type: 'create',
        open: false,
        id: null
    });

    const [copy, { data: copyData, isSuccess, isLoading: isLoadingCopy }] = useCopyEAMutation();
    const [previewDiscardChanges] = usePreviewFormDiscardChangesMutation();

    const [area, setArea] = useState<{ id: number | null; type: string }>({ id: null, type: '' });
    const [kickOffPreview, setKickOffPreview] = useState<{ open: boolean; expenseAreaId: number | null }>({
        open: false,
        expenseAreaId: null
    });
    const [spqPreview, setSpqPreview] = useState<{ open: boolean; expenseAreaId: number | null }>({
        open: false,
        expenseAreaId: null
    });
    const [scoreCardPreview, setScoreCardPreview] = useState<{ open: boolean; expenseAreaId: number | null }>({
        open: false,
        expenseAreaId: null
    });

    const [activePopoverIndex, setActivePopoverIndex] = useState<number | null>(null);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounceSearch(search);
    const [searchParams] = useSearchParams();
    const EAId = searchParams.get('id');
    const EAModalType = searchParams.get('type');

    useEffect(() => {
        if (isSuccess && copyData) {
            message.success(copyData?.message);
            refetch();
        }
    }, [isSuccess]);

    useEffect(() => {
        setVariables({
            ...variables,
            filter: { ...variables.filter, search: debouncedSearch }
        });
    }, [debouncedSearch]);

    useEffect(() => {
        setVariables((prev) => ({ ...prev, page: 1 }));
    }, [search]);

    useEffect(() => {
        if (EAId && EAModalType) {
            setEditModal({ type: EAModalType, open: true, id: +EAId });
        }
    }, [EAId, EAModalType]);

    const editFunc = (id: number) => {
        setEditModal({ open: true, type: 'edit', id: id });
    };

    const copyFunc = (id: number) => {
        copy(id);
    };

    const previewFunc = async (id: number, formType?: string) => {
        if (formType === 'kick-off-questionnaire') {
            try {
                await previewDiscardChanges({
                    expense_area_id: id,
                    type: formType
                }).unwrap();
                setKickOffPreview({ open: true, expenseAreaId: id });
            } catch (error: any) {
                message.error(error?.data?.message ?? 'Could not open preview.');
            }
            return;
        }

        if (formType === 'supplier-profile-questionnaire') {
            try {
                await previewDiscardChanges({
                    expense_area_id: id,
                    type: formType
                }).unwrap();
                setSpqPreview({ open: true, expenseAreaId: id });
            } catch (error: any) {
                message.error(error?.data?.message ?? 'Could not open preview.');
            }
            return;
        }

        if (formType === 'score-card') {
            try {
                await previewDiscardChanges({
                    expense_area_id: id,
                    type: formType
                }).unwrap();
                setScoreCardPreview({ open: true, expenseAreaId: id });
            } catch (error: any) {
                message.error(error?.data?.message ?? 'Could not open preview.');
            }
        }
    };

    const dots = (id: number, status: string) => (
        <Dropdown
            overlay={getMenu(id, status, setArea, editFunc, '', copyFunc)}
            trigger={['click']}
            placement="bottomRight"
        >
            <div
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <MoreOutlined style={{ fontSize: 20, cursor: 'pointer' }} />
            </div>
        </Dropdown>
    );

    const formDots = (id: number, type?: string) => (
        <Dropdown
            overlay={getMenu(id, 'form', setArea, setEditModal, type, copyFunc, previewFunc)}
            trigger={['click']}
            placement="bottomRight"
        >
            <div
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <Dots />
            </div>
        </Dropdown>
    );

    const { tableProps } = useTableDataSourceWithHooks<ExpenseAreaI>({
        columns: [
            {
                dataIndex: 'name',
                title: 'Name',
                sorter: true,
                width: 120,
                sortIcon: ({ sortOrder }) => {
                    if (sortOrder === 'ascend') {
                        return <ArrowUp />;
                    }
                    if (sortOrder === 'descend') {
                        return <ArrowDown />;
                    }
                }
            },
            {
                dataIndex: 'components',
                title: 'Components',
                sorter: false,
                width: 230,
                render: (component, record) => (
                    <div className="EA components">
                        {component?.slice(0, 1).map((elem: ComponentExpenseArea, index: number) => (
                            <div className="EA__component" key={index}>
                                <Tooltip title={elem.description}>
                                    <div>
                                        <InfoIcon />
                                    </div>
                                </Tooltip>
                                <EllipsisTooltipCell text={elem?.name} />
                                <div className="EA__people">
                                    <PeopleIcon fill="#4D5E93" /> {elem.number_of_suppliers}
                                </div>
                            </div>
                        ))}

                        {component && component.length > 1 && (
                            <div className="EA__group">
                                <div className="EA__component">
                                    <Tooltip title={component[1].description}>
                                        <div>
                                            <InfoIcon />
                                        </div>
                                    </Tooltip>
                                    {component[1].name}
                                    <div className="EA__people">
                                        <PeopleIcon fill="#4D5E93" /> {component[1].number_of_suppliers}
                                    </div>
                                </div>

                                {component.length > 2 && (
                                    <Popover
                                        trigger="click"
                                        placement="top"
                                        open={activePopoverIndex === record?.id}
                                        onOpenChange={(visible) => {
                                            setActivePopoverIndex(visible ? record?.id : null);
                                        }}
                                        content={
                                            <div className="EA__popover-content" onClick={(e) => e.stopPropagation()}>
                                                {component.slice(2).map((elem: ComponentExpenseArea, index: number) => (
                                                    <div key={index} className="EA__component">
                                                        <Tooltip title={elem.description}>
                                                            <div style={{ height: '15px' }}>
                                                                <InfoIcon />
                                                            </div>
                                                        </Tooltip>
                                                        <span>{elem.name}</span>
                                                        <div className="EA__people">
                                                            <PeopleIcon fill="#4D5E93" /> {elem.number_of_suppliers}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        }
                                    >
                                        <div
                                            className={`EA__component ${
                                                activePopoverIndex === record?.id ? 'active' : ''
                                            }`}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            +{component.length - 2}
                                        </div>
                                    </Popover>
                                )}
                            </div>
                        )}
                    </div>
                )
            },
            {
                dataIndex: 'forms',
                title: 'Documents',
                sorter: false,
                width: 500,
                render: (form, record) => (
                    <div className="EA">
                        <div className="EA__wrapper">
                            <div className="EA__title">
                                {form?.['supplier-profile-questionnaire']?.name}{' '}
                                {formDots(record?.id, 'supplier-profile-questionnaire')}
                            </div>
                            {form?.['supplier-profile-questionnaire']?.status === 'draft' ? (
                                <div className="EA__add-info">
                                    <div className="EA__status">Draft</div>
                                </div>
                            ) : (
                                <div className="EA__add-info">
                                    <Tooltip
                                        title={`Last update on ${form?.['supplier-profile-questionnaire']?.updated_at}`}
                                    >
                                        <div className="EA__time">
                                            <img src={clocoIcon} />{' '}
                                            {form?.['supplier-profile-questionnaire']?.updated_at}
                                        </div>
                                    </Tooltip>
                                    {!!form?.['supplier-profile-questionnaire']?.number_of_suppliers && (
                                        <div className="EA__people">
                                            <PeopleIcon fill="#4D5E93" />{' '}
                                            {form?.['supplier-profile-questionnaire']?.number_of_suppliers}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="EA__wrapper">
                            <div className="EA__title">
                                {form?.['kick-off-questionnaire']?.name}{' '}
                                {formDots(record?.id, 'kick-off-questionnaire')}
                            </div>
                            {form?.['kick-off-questionnaire']?.status === 'draft' ? (
                                <div className="EA__add-info">
                                    <div className="EA__status">Draft</div>
                                </div>
                            ) : (
                                <div className="EA__add-info">
                                    <Tooltip title={`Last update on ${form?.['kick-off-questionnaire']?.updated_at}`}>
                                        <div className="EA__time">
                                            <img src={clocoIcon} /> {form?.['kick-off-questionnaire']?.updated_at}
                                        </div>
                                    </Tooltip>
                                    {!!form?.['kick-off-questionnaire']?.number_of_suppliers && (
                                        <div className="EA__people">
                                            <PeopleIcon fill="#4D5E93" />{' '}
                                            {form?.['kick-off-questionnaire']?.number_of_suppliers}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="EA__wrapper">
                            <div className="EA__title">
                                {form?.['score-card']?.name} {formDots(record?.id, 'score-card')}
                            </div>
                            {form?.['score-card']?.status === 'draft' ? (
                                <div className="EA__add-info">
                                    <div className="EA__status">Draft</div>
                                </div>
                            ) : (
                                <div className="EA__add-info">
                                    <Tooltip title={`Last update on ${form?.['score-card']?.updated_at}`}>
                                        <div className="EA__time">
                                            <img src={clocoIcon} /> {form?.['score-card']?.updated_at}
                                        </div>
                                    </Tooltip>
                                    {!!form?.['score-card']?.number_of_suppliers && (
                                        <div className="EA__people">
                                            <PeopleIcon fill="#4D5E93" /> {form?.['score-card']?.number_of_suppliers}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )
            },

            {
                dataIndex: 'status',
                title: 'Status',
                sorter: true,
                width: 60,
                render: (_status, record) => (
                    <div className={`expense-area-status ${record?.status}`}>
                        <div className={`expense-area-status__round ${record?.status}`}></div>
                        {record.status_name}
                    </div>
                ),

                sortIcon: ({ sortOrder }) => {
                    if (sortOrder === 'ascend') {
                        return <ArrowUp />;
                    }
                    if (sortOrder === 'descend') {
                        return <ArrowDown />;
                    }
                }
            },

            {
                title: ' ',
                width: 40,
                key: 'x',
                render: (_, record) => <div className="expense-area-dots">{dots(record?.id, record?.status)}</div>,
                align: 'right'
            }
        ],
        rowKey: 'id',
        data: {
            rows: data?.data,
            loading: isLoading || isFetching || isLoadingCopy,
            total: data?.meta?.total
        },
        paginationConfig: { defaultPageSize: 10, showSizeChanger: false, position: ['bottomCenter'] },
        onQueryVariableChange: (options) => {
            setVariables({
                ...variables,
                page: options?.page || 1,
                per_page: options?.limit || 10,
                sort_field: options?.sortField || variables.sort_field,
                sort_direction: options?.sortDirection || variables.sort_direction,
                filter: {
                    ...variables.filter
                }
            });
        }
    });

    return (
        <>
            <div className="header-actions">
                <div className="header-actions__row">
                    <InputSearch handleChange={(value) => setSearch(value)} />
                </div>

                <Button
                    type="primary"
                    onClick={() => {
                        setEditModal({ type: 'create', open: true, id: null });
                    }}
                    icon={<PlusCircleOutlined />}
                >
                    Add
                </Button>
            </div>

            <Table<ExpenseAreaI>
                {...tableProps}
                selectedRowKeys={selectedRowKeys}
                setSelectedRowKeys={setSelectedRowKeys}
                scroll={{ y: '70vh' }}
                locale={{
                    emptyText: variables.filter?.search ? (
                        <Empty
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'column',
                                gap: '24px',
                                margin: '50px 0'
                            }}
                            image={<NoResults />}
                            description={
                                <div
                                    style={{
                                        maxWidth: '390px'
                                    }}
                                >
                                    <Typography.Title level={4}>No results found</Typography.Title>
                                    <Typography.Paragraph type="secondary">
                                        Try adjusting your search to find what you are looking for
                                    </Typography.Paragraph>
                                </div>
                            }
                        />
                    ) : (
                        <Empty
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexDirection: 'column',
                                gap: '24px',
                                margin: '50px 0'
                            }}
                            image={<NoUsers />}
                            description={
                                <div
                                    style={{
                                        maxWidth: '390px'
                                    }}
                                >
                                    <Typography.Title level={4}>No expense areas yet</Typography.Title>
                                    <Typography.Paragraph type="secondary">
                                        Add a new user to start to working with them
                                    </Typography.Paragraph>
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            setEditModal({ type: 'create', open: true, id: null });
                                        }}
                                        icon={<PlusCircleOutlined />}
                                    >
                                        Add User
                                    </Button>
                                </div>
                            }
                        />
                    )
                }}
            />
            {editModal.open && (
                <AddExpenseAreaModal
                    openModal={editModal.open}
                    setOpenModal={setEditModal}
                    type={editModal.type}
                    id={editModal?.id}
                    refetch={refetch}
                />
            )}

            {!!area?.id && (
                <ChangeStatusModal
                    id={area?.id}
                    setArea={setArea}
                    refetch={refetch}
                    type={area?.type}
                    AllEAs={data?.data}
                />
            )}
            {kickOffPreview.open && kickOffPreview.expenseAreaId != null && (
                <AdminKickOffPreviewModal
                    openModal={kickOffPreview.open}
                    setOpenModal={(open) =>
                        setKickOffPreview((prev) => ({
                            ...prev,
                            open: typeof open === 'function' ? open(prev.open) : open
                        }))
                    }
                    expenseAreaId={kickOffPreview.expenseAreaId}
                />
            )}
            {spqPreview.open && spqPreview.expenseAreaId != null && (
                <AdminSupplierProfilePreviewModal
                    openModal={spqPreview.open}
                    setOpenModal={(open) =>
                        setSpqPreview((prev) => ({
                            ...prev,
                            open: typeof open === 'function' ? open(prev.open) : open
                        }))
                    }
                    expenseAreaId={spqPreview.expenseAreaId}
                />
            )}
            {scoreCardPreview.open && scoreCardPreview.expenseAreaId != null && (
                <AdminScoreCardPreviewModal
                    openModal={scoreCardPreview.open}
                    setOpenModal={(open) =>
                        setScoreCardPreview((prev) => ({
                            ...prev,
                            open: typeof open === 'function' ? open(prev.open) : open
                        }))
                    }
                    expenseAreaId={scoreCardPreview.expenseAreaId}
                />
            )}
        </>
    );
};
